import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { SITE } from "@/data/site";
import { Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full border border-neutral-800 bg-neutral-900 px-4 py-3 text-white placeholder-neutral-600 outline-none transition-colors focus:border-orange-500";

export default function Contact() {
  useSeo(
    "Contact | Eastern Ontario CNC Machine Shop | Edgeworks Manufacturing",
    "Contact Edgeworks Manufacturing for CNC machining in the Cornwall area and Eastern Ontario. Email quotes@edgeworksmfg.ca or send a message to get started."
  );

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. I'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="border-b border-neutral-800 bg-neutral-900">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">Contact</p>
          <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
            Get in Touch
          </h1>
          <p className="mt-4 max-w-2xl text-neutral-300">
            Have a drawing, model, or sample part? Send it over and I'll take a look.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <Mail className="mt-1 shrink-0 text-orange-500" size={22} />
              <div>
                <p className="font-mono text-xs uppercase tracking-wide text-neutral-500">Email</p>
                <a href={`mailto:${SITE.email}`} className="text-lg text-white hover:text-orange-500" data-testid="contact-email">
                  {SITE.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="mt-1 shrink-0 text-orange-500" size={22} />
              <div>
                <p className="font-mono text-xs uppercase tracking-wide text-neutral-500">Service Area</p>
                <p className="text-neutral-200">{SITE.area}</p>
              </div>
            </div>
            <Link
              to="/request-a-quote"
              data-testid="contact-quote-btn"
              className="inline-flex items-center gap-2 bg-orange-500 px-6 py-3 font-semibold text-white transition-colors hover:bg-orange-600"
            >
              Request a Quote <ArrowRight size={18} />
            </Link>
          </div>

          <form onSubmit={onSubmit} className="border border-neutral-800 bg-neutral-950 p-6 sm:p-8" data-testid="contact-form">
            <div className="grid gap-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-300">Name</label>
                <input name="name" required value={form.name} onChange={onChange} className={field} data-testid="contact-name" />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-300">Email</label>
                  <input type="email" name="email" required value={form.email} onChange={onChange} className={field} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-300">Phone</label>
                  <input name="phone" value={form.phone} onChange={onChange} className={field} data-testid="contact-phone" />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-300">Message</label>
                <textarea name="message" required rows={6} value={form.message} onChange={onChange} className={field} data-testid="contact-message" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 px-6 py-3.5 font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
